import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { RefreshIcon, PencilIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { formatDateTime } from '../../../config/appConfig';
import { API_BASE_URL } from '../../../config/apiConfig';
import EmailBodyView from './EmailBodyView';
import {
  EMAIL_MESSAGE_CANVAS_CLASS,
  EMAIL_MESSAGE_PLAIN_TEXT_CLASS,
} from '../utils/emailMessageCanvas';
import {
  fetchOutboxApi,
  resendOutboxApi,
  type EmailOutboxDetail,
  type EmailOutboxStatus,
} from '../useData';
import { navigateToCompose, type ComposeReturnTarget } from '../utils/composeNavigation';
import { OutboxSendSchedule } from './OutboxSendSchedule';
import { outboxQueuedSuccessMessage } from '../utils/outboxMessages';

function formatAddressList(addrs?: string[]): string {
  return (addrs ?? []).filter(Boolean).join(', ');
}

function statusLabel(status: EmailOutboxStatus): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'sending':
      return 'Sending';
    case 'failed':
      return 'Failed';
    case 'sent':
      return 'Sent';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
}

interface OutboxDetailModalProps {
  outboxId: number | null;
  isOpen: boolean;
  onClose: () => void;
  composeReturnTo: ComposeReturnTarget;
  onChanged?: () => void;
}

const OutboxDetailModal: React.FC<OutboxDetailModalProps> = ({
  outboxId,
  isOpen,
  onClose,
  composeReturnTo,
  onChanged,
}) => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<EmailOutboxDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!isOpen || outboxId == null) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void fetchOutboxApi(outboxId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) {
          showError('Failed to load message');
          onClose();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, outboxId, onClose, showError]);

  const handleResend = async () => {
    if (!detail) return;
    setResending(true);
    try {
      const result = await resendOutboxApi(detail.id);
      showSuccess(outboxQueuedSuccessMessage(result.undoDelaySeconds ?? 0));
      onChanged?.();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to resend message');
    } finally {
      setResending(false);
    }
  };

  const handleEditInCompose = () => {
    if (!detail) return;
    navigateToCompose(
      navigate,
      '/emails/compose',
      { outboxId: detail.id },
      composeReturnTo,
    );
    onClose();
  };

  const accountLabel =
    detail?.emailAccount?.displayName ||
    detail?.emailAccount?.emailAddress ||
    (detail ? `Account #${detail.emailAccountId}` : '');
  const payload = detail?.payload;
  const bodyHtml = payload?.html ?? '';
  const bodyText = payload?.text ?? '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>{detail?.subject || '(no subject)'}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        {loading && (
          <div className={cn('py-8 text-center text-sm', colors.textMuted)}>Loading message…</div>
        )}
        {!loading && detail && (
          <div className="space-y-4 min-w-0">
            <div className={cn('rounded-lg border px-4 py-3 space-y-2 text-sm', colors.surface, colors.border)}>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded',
                    detail.status === 'failed'
                      ? 'bg-red-500/15 text-red-700 dark:text-red-300'
                      : detail.status === 'cancelled'
                        ? 'bg-slate-500/15 text-slate-600 dark:text-slate-400'
                        : detail.status === 'sending'
                          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                          : 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
                  )}
                >
                  {statusLabel(detail.status)}
                </span>
                <span className={cn('text-xs', colors.textMuted)}>
                  {formatDateTime(detail.createdAt)}
                </span>
              </div>
              <div>
                <span className={cn('font-medium', colors.textMuted)}>From: </span>
                <span className={colors.text}>{accountLabel}</span>
              </div>
              {formatAddressList(detail.recipients?.to) && (
                <div>
                  <span className={cn('font-medium', colors.textMuted)}>To: </span>
                  <span className={colors.text}>{formatAddressList(detail.recipients?.to)}</span>
                </div>
              )}
              {formatAddressList(detail.recipients?.cc) && (
                <div>
                  <span className={cn('font-medium', colors.textMuted)}>Cc: </span>
                  <span className={colors.text}>{formatAddressList(detail.recipients?.cc)}</span>
                </div>
              )}
              {formatAddressList(detail.recipients?.bcc) && (
                <div>
                  <span className={cn('font-medium', colors.textMuted)}>Bcc: </span>
                  <span className={colors.text}>{formatAddressList(detail.recipients?.bcc)}</span>
                </div>
              )}
              {(detail.status === 'queued' || detail.status === 'sending') && (
                <OutboxSendSchedule
                  scheduledSendAt={detail.scheduledSendAt}
                  status={detail.status}
                  variant="inline"
                  className="pt-1"
                />
              )}
              {detail.status === 'cancelled' && (
                <p className={cn('text-xs pt-1', colors.textMuted)}>
                  Send cancelled before delivery
                  {detail.updatedAt && (
                    <span> · {formatDateTime(detail.updatedAt)}</span>
                  )}
                </p>
              )}
            </div>

            {detail.errorMessage && (
              <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300 mb-1">
                  Send error
                </p>
                <p className="text-xs text-red-700 dark:text-red-300 break-words whitespace-pre-wrap">
                  {detail.errorMessage}
                </p>
              </div>
            )}

            <div className={cn('px-4 py-3 min-w-0', EMAIL_MESSAGE_CANVAS_CLASS)}>
              {bodyHtml ? (
                <EmailBodyView
                  bodyHtml={bodyHtml}
                  emailId={0}
                  apiBaseUrl={API_BASE_URL}
                />
              ) : (
                <pre className={EMAIL_MESSAGE_PLAIN_TEXT_CLASS}>
                  {bodyText || '(no content)'}
                </pre>
              )}
            </div>

            {(payload?.attachments?.length ?? 0) > 0 && (
              <div className={cn('text-xs', colors.textMuted)}>
                {payload!.attachments!.length} attachment{payload!.attachments!.length === 1 ? '' : 's'} included
              </div>
            )}
          </div>
        )}
      </ModalContent>
      <ModalFooter className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
        {detail && (
          <Button type="button" variant="outline" size="sm" onClick={handleEditInCompose}>
            <PencilIcon className="w-4 h-4" />
            Edit in compose
          </Button>
        )}
        {detail?.status === 'failed' && (
          <Button type="button" size="sm" loading={resending} onClick={handleResend}>
            <RefreshIcon className="w-4 h-4" />
            Resend
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default OutboxDetailModal;
